// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import { Warning as WarningIcon } from '@/constants/hardfin-standard-icons';
import { cancelPurchaseOrder } from '@/purchase-order/api/cancel-purchase-order';
import { PurchaseOrder } from '@/purchase-order/api/types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  reason: z.string().min(1, 'A reason is required to cancel this order'),
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  isOpen: boolean;
  purchaseOrderKey: string;
  poNumber: string;
  onClose: () => void;
  onSuccess: (updatedPurchaseOrder: PurchaseOrder) => void;
}

/**
 * Dialog component for cancelling a purchase order.
 * Requires a reason for cancellation before submitting.
 */
export function PurchaseOrderCancelDialog(props: Props) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSnackbar, setShowSnackbar] = useState(false);

  const cancelMutation = useMutation({
    mutationFn: (reason: string) =>
      cancelPurchaseOrder(props.purchaseOrderKey, reason),
    onSuccess: (updatedPurchaseOrder) => {
      close();
      props.onSuccess(updatedPurchaseOrder);
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setErrorMessage(curated);
        setShowSnackbar(false);
      } else {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        setErrorMessage(null);
        setShowSnackbar(true);
      }
    },
  });

  const { control, reset, handleSubmit, formState } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reason: '',
    },
  });

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    return cancelMutation.mutate(data.reason);
  };

  const close = () => {
    props.onClose();
    reset();
    setErrorMessage(null);
    setShowSnackbar(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={close}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
        sx: {
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2,
        }}
      >
        Cancel purchase order
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Alert
          severity="warning"
          icon={<WarningIcon fontSize="medium" />}
          sx={{
            marginY: 3,
            alignItems: 'center',
          }}
        >
          <AlertTitle variant="h6">
            Are you sure you want to cancel order{' '}
            <strong>{props.poNumber}</strong>?
          </AlertTitle>
          <Typography variant="body1">
            Cancelling this order is permanent and cannot be undone.
          </Typography>
        </Alert>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ marginTop: 2, marginBottom: 1 }}
        >
          Cancellation reason
        </Typography>

        <FormTextField
          control={control}
          name="reason"
          fullWidth={true}
          isMultiline={true}
          minimumRows={3}
          placeholder="Please provide a reason for cancellation"
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button onClick={close} variant="outlined">
          No, keep order
        </Button>
        <Button
          type="submit"
          color="error"
          variant="contained"
          disabled={formState.isSubmitting}
          sx={{ ml: 1.5 }}
        >
          Yes, cancel order
        </Button>
      </DialogActions>
      <SupportSnackbar
        isOpen={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
          cancelMutation.reset();
        }}
        message="An error occurred while cancelling the purchase order."
      />
    </Dialog>
  );
}
